import { MacroChannelKey } from "../../constants/MacroChannels";
import { NodeSubType } from "../../models/nodeSubType";
import { ActionTypeBackend, FrontendNodeType } from "../../ui-components/macro-builder/macroNode.types";

// ─── Attachment enums ─────────────────────────────────────────────────────────

export enum AttachmentType {
  NONE = "None",
  IMAGE = "Image",
  VIDEO = "Video",
  DOCUMENT = "Document",
}

export enum HeaderMediaType {
  TEXT = "text",
  IMAGE = "image",
  VIDEO = "video",
  DOCUMENT = "document",
}

/** Maps an AttachmentType (display name) to its HeaderMediaType (wire format). */
export const ATTACHMENT_TYPE_TO_HEADER: Partial<Record<AttachmentType, HeaderMediaType>> = {
  [AttachmentType.IMAGE]: HeaderMediaType.IMAGE,
  [AttachmentType.VIDEO]: HeaderMediaType.VIDEO,
  [AttachmentType.DOCUMENT]: HeaderMediaType.DOCUMENT,
};

/** Reverse map — HeaderMediaType (wire format) back to AttachmentType (display name). */
export const HEADER_TO_ATTACHMENT_TYPE: Partial<Record<HeaderMediaType, AttachmentType>> = {
  [HeaderMediaType.IMAGE]: AttachmentType.IMAGE,
  [HeaderMediaType.VIDEO]: AttachmentType.VIDEO,
  [HeaderMediaType.DOCUMENT]: AttachmentType.DOCUMENT,
};

// ─── Attachment models ────────────────────────────────────────────────────────

export interface AttachmentConfig {
  type: AttachmentType;
  mediaUrl?: string;
  mediaName?: string;
  mediaSize?: string;
}

export interface AttachmentPickerValue {
  type: AttachmentType;
  attachments: AttachmentConfig[];
}

export interface MediaUrl {
  fileName: string;
  type: HeaderMediaType;
  path: string;
  size?: string;
}

export interface HeaderConfig {
  mediaUrls?: MediaUrl[];
  type: HeaderMediaType | string;
  body?: string;
}

export interface ButtonConfig {
  type: string;
  key: string;
  text: string;
  id: string;
  value: string;
}

export interface WhatsappTemplateConfig {
  id: string;
  title: string;
  bikCategory: string;
  category: "marketing" | "utility" | string;
  label: string;
}

export interface ResponseBlockConfig {
  body: string;
  header: HeaderConfig;
  footer?: string;
  buttons?: ButtonConfig[];
  whatsappTemplate?: WhatsappTemplateConfig;
}

// ─── Editor state models ──────────────────────────────────────────────────────

/** Typed body-editor state — key matches the RTE `uniqueId` prop ("bodyText"). */
export type BodyContentState = { readonly bodyText: string };

/** Typed selected-variables state — key matches the RTE `uniqueId` prop ("bodyText"). */
export type SelectedFieldsState = { readonly bodyText: string[] };

// ─── Payload models ───────────────────────────────────────────────────────────

export interface ResponseBlockFrontendPayload {
  type: FrontendNodeType;
  title: string;
  subType: NodeSubType;
  description: string;
  media: string;
  mediaUrl: string;
  attachments: AttachmentConfig[];
}

export interface ResponseBlockBackendPayload {
  channel: MacroChannelKey;
  type: ActionTypeBackend;
  subType: NodeSubType;
  message: ResponseBlockConfig;
  next: string;
}

// ─── Node data models ─────────────────────────────────────────────────────────

export interface ResponseBlockNodeConf {
  message?: ResponseBlockConfig;
  next?: string;
}

export interface ResponseBlockNodeData {
  nodeId: string;
  /** Snake-case `subtype` as received from backend. */
  subtype?: NodeSubType;
  conf?: ResponseBlockNodeConf;
}

// ─── Component config & props ─────────────────────────────────────────────────

export interface ResponseBlockComponentConfig {
  automationChannel: MacroChannelKey;
  subType?: NodeSubType;
}

export interface ResponseBlockProps {
  saveTo: string;
  setSaveTo: (value: string) => void;
  storeId: string;
  flowId: string;
  config: ResponseBlockComponentConfig;
  nodeData: ResponseBlockNodeData;
  closeModal: () => void;
  setLoading: (v: boolean) => void;
  channel?: MacroChannelKey;
}

/**
 * Channels with 1000 character limit for message body
 */
export const CHANNELS_WITH_1000_CHAR_LIMIT = [
  MacroChannelKey.INSTAGRAM,
  MacroChannelKey.MESSENGER,
  MacroChannelKey.LIVECHAT,
];

